import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { progressAPI, reportsAPI, bookmarksAPI } from '../../services/api';

const partsInfo = [
  { number: 1, title: 'Gestational & Natal History', description: 'Infections, medications, maternal conditions, and birth weight', questionCount: 17 },
  { number: 2, title: 'Postnatal History', description: 'Weight gain, cyanosis, tachypnea, exercise intolerance', questionCount: 17 },
  { number: 3, title: 'Chief Complaints & Family History', description: 'Heart murmur, chest pain, syncope, palpitation, family history', questionCount: 18 },
  { number: 4, title: 'Growth, Inspection & Syndromes', description: 'Growth patterns, physical inspection, associated syndromes', questionCount: 18 },
  { number: 5, title: 'Palpation', description: 'Peripheral pulses, chest palpation, thrills', questionCount: 15 },
  { number: 6, title: 'Blood Pressure Measurement', description: 'BP methodology, cuff selection, normative standards', questionCount: 18 },
  { number: 7, title: 'Heart Sounds', description: 'S1, S2 splitting, S3, S4, gallop rhythms, clicks', questionCount: 18 },
  { number: 8, title: 'Systolic Murmurs', description: 'Classification, types, location, differential diagnosis', questionCount: 20 },
  { number: 9, title: 'Diastolic, Continuous & Innocent Murmurs', description: 'Diastolic murmurs, continuous murmurs, innocent murmurs', questionCount: 18 },
  { number: 10, title: 'Neonatal Cardiac Examination', description: 'Normal and abnormal findings in newborns', questionCount: 15 }
];

function Dashboard() {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [progress, setProgress] = useState({});
  const [reports, setReports] = useState([]);
  const [showReports, setShowReports] = useState(false);
  const [bookmarks, setBookmarks] = useState([]);
  const [showBookmarks, setShowBookmarks] = useState(false);

  useEffect(() => {
    // Load progress from API
    progressAPI.getAll()
      .then(data => {
        if (data.progress) {
          setProgress(data.progress);
        }
      })
      .catch(err => console.error('Failed to load progress:', err));

    // Load reports from API
    reportsAPI.getAll()
      .then(data => {
        if (data.reports) {
          setReports(data.reports);
        }
      })
      .catch(err => console.error('Failed to load reports:', err));

    // Load bookmarks from API
    bookmarksAPI.getAll()
      .then(data => {
        if (data.bookmarks) {
          setBookmarks(data.bookmarks);
        }
      })
      .catch(err => console.error('Failed to load bookmarks:', err));
  }, [user]);

  const handlePartClick = (partNumber) => {
    navigate(`/assessment/${partNumber}`);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getPartStatus = (partNumber) => {
    const partProgress = progress[partNumber];
    if (!partProgress) return null;
    return {
      completed: partProgress.completed,
      score: partProgress.score,
      total: partProgress.total
    };
  };

  const clearReports = async () => {
    if (window.confirm('Clear all reports? This cannot be undone.')) {
      try {
        await reportsAPI.deleteAll();
        setReports([]);
      } catch (err) {
        console.error('Failed to clear reports:', err);
      }
    }
  };

  const resolveReport = async (id) => {
    try {
      await reportsAPI.resolve(id);
      setReports(reports.map(r => r.id === id ? { ...r, resolved: true } : r));
    } catch (err) {
      console.error('Failed to resolve report:', err);
    }
  };

  const deleteReport = async (id) => {
    try {
      await reportsAPI.delete(id);
      setReports(reports.filter(r => r.id !== id));
    } catch (err) {
      console.error('Failed to delete report:', err);
    }
  };

  const removeBookmark = async (questionId) => {
    try {
      await bookmarksAPI.remove(questionId);
      setBookmarks(bookmarks.filter(b => b.question_id !== questionId));
    } catch (err) {
      console.error('Failed to remove bookmark:', err);
    }
  };

  const navigateToBookmark = (bookmark) => {
    setShowBookmarks(false);
    navigate(`/assessment/${bookmark.part_number}`);
  };

  const getPartTitle = (partNumber) => {
    const part = partsInfo.find(p => p.number === partNumber);
    return part ? part.title : `Part ${partNumber}`;
  };

  const exportReports = () => {
    const dataStr = JSON.stringify(reports, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `question-reports-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
  };

  const unresolvedReports = reports.filter(r => !r.resolved);

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Pediatric Cardiac Assessment</h1>
        <div className="user-info">
          <span>Welcome, {user?.name}</span>
          <button
            className="btn btn-secondary theme-toggle"
            onClick={toggleTheme}
            aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
            title={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
          >
            {theme === 'light' ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/>
              </svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="5"/>
                <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
              </svg>
            )}
          </button>
          {user?.isAdmin && (
            <button className="btn btn-secondary" onClick={() => navigate('/analytics')}>
              View Analytics
            </button>
          )}
          <button className="btn btn-secondary" onClick={handleLogout}>
            Sign Out
          </button>
        </div>
      </div>

      <p style={{ marginBottom: '20px', color: 'var(--text-secondary)' }}>
        Select a part to begin the assessment, or create a custom quiz with filters.
      </p>

      <button
        className="btn btn-primary create-quiz-btn"
        onClick={() => navigate('/quiz-setup')}
        style={{ marginBottom: '30px', display: 'block' }}
      >
        Create Custom Quiz
      </button>

      {unresolvedReports.length > 0 && (
        <div className="reports-banner" onClick={() => setShowReports(true)}>
          <span className="reports-icon">📋</span>
          <span>{unresolvedReports.length} question {unresolvedReports.length === 1 ? 'report' : 'reports'} to review</span>
        </div>
      )}

      {bookmarks.length > 0 && (
        <div className="bookmarks-banner" onClick={() => setShowBookmarks(true)}>
          <span className="bookmarks-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17 3H7c-1.1 0-2 .9-2 2v16l7-3 7 3V5c0-1.1-.9-2-2-2z"/>
            </svg>
          </span>
          <span>{bookmarks.length} bookmarked {bookmarks.length === 1 ? 'question' : 'questions'} for review</span>
        </div>
      )}

      <div className="parts-grid">
        {partsInfo.map((part) => {
          const status = getPartStatus(part.number);
          const isAvailable = part.number <= 10; // All 10 parts now available

          return (
            <div
              key={part.number}
              className="part-card"
              onClick={() => isAvailable && handlePartClick(part.number)}
              style={{
                opacity: isAvailable ? 1 : 0.5,
                cursor: isAvailable ? 'pointer' : 'not-allowed'
              }}
            >
              <h3>Part {part.number}: {part.title}</h3>
              <p>{part.description}</p>
              <div className="part-status">
                <span>{part.questionCount} questions</span>
                {status?.completed ? (
                  <span className="status-complete">
                    ✓ {status.score}/{status.total} ({Math.round(status.score / status.total * 100)}%)
                  </span>
                ) : isAvailable ? (
                  <span className="status-incomplete">Not started</span>
                ) : (
                  <span className="status-incomplete">Coming soon</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showReports && (
        <div className="modal-overlay" onClick={() => setShowReports(false)}>
          <div className="modal-content reports-modal" onClick={e => e.stopPropagation()}>
            <h3>Question Reports ({reports.length})</h3>
            <div className="reports-actions">
              <button className="btn btn-secondary" onClick={exportReports}>
                Export JSON
              </button>
              <button className="btn btn-secondary" onClick={clearReports} style={{ color: '#dc3545' }}>
                Clear All
              </button>
            </div>
            <div className="reports-list">
              {reports.map((report) => (
                <div key={report.id} className={`report-item ${report.resolved ? 'resolved' : ''}`}>
                  <div className="report-header">
                    <strong>{report.question_id}</strong>
                    <span className="report-date">
                      {new Date(report.reported_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="report-stem">{report.question_stem}</div>
                  {report.comment && (
                    <div className="report-comment">"{report.comment}"</div>
                  )}
                  <div className="report-footer">
                    <span className="report-by">— {report.reported_by}</span>
                    <div className="report-actions">
                      {!report.resolved && (
                        <button className="btn-small btn-resolve" onClick={() => resolveReport(report.id)}>
                          ✓ Resolve
                        </button>
                      )}
                      <button className="btn-small btn-delete" onClick={() => deleteReport(report.id)}>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="modal-buttons">
              <button className="btn btn-primary" onClick={() => setShowReports(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showBookmarks && (
        <div className="modal-overlay" onClick={() => setShowBookmarks(false)}>
          <div className="modal-content bookmarks-modal" onClick={e => e.stopPropagation()}>
            <h3>Bookmarked Questions ({bookmarks.length})</h3>
            <p className="bookmarks-hint">Click a question to navigate to it</p>
            <div className="bookmarks-list">
              {bookmarks.map((bookmark) => (
                <div key={bookmark.question_id} className="bookmark-item">
                  <div
                    className="bookmark-info"
                    onClick={() => navigateToBookmark(bookmark)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && navigateToBookmark(bookmark)}
                  >
                    <strong>{bookmark.question_id}</strong>
                    <span className="bookmark-part">Part {bookmark.part_number}: {getPartTitle(bookmark.part_number)}</span>
                    <span className="bookmark-date">
                      {new Date(bookmark.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <button
                    className="btn-small btn-delete"
                    onClick={() => removeBookmark(bookmark.question_id)}
                    aria-label="Remove bookmark"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
            <div className="modal-buttons">
              <button className="btn btn-primary" onClick={() => setShowBookmarks(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
